import React, { ChangeEvent, DragEvent, useRef, useState } from "react";
import { ImagePlus, Trash2 } from "lucide-react";
import { PortalActivityImage } from "../api/contracts";

const ACTIVITY_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);
const MAX_ACTIVITY_IMAGE_BYTES = 10 * 1024 * 1024;

export function activityImageValidationMessage(file: File): string | null {
  if (!ACTIVITY_IMAGE_TYPES.has(file.type)) {
    return `${file.name} must be a JPEG, PNG, WebP, or GIF image.`;
  }
  if (file.size > MAX_ACTIVITY_IMAGE_BYTES) {
    return `${file.name} must be 10 MB or smaller.`;
  }
  if (file.size === 0) {
    return `${file.name} is empty.`;
  }
  return null;
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("The image could not be read."));
    reader.onload = () => {
      const value = typeof reader.result === "string" ? reader.result : "";
      const separator = value.indexOf(",");
      if (separator < 0) {
        reject(new Error("The image could not be read."));
        return;
      }
      resolve(value.slice(separator + 1));
    };
    reader.readAsDataURL(file);
  });
}

export function ActivityImageGallery({
  images,
}: {
  images: readonly PortalActivityImage[];
}) {
  const visibleImages = [...(images ?? [])]
    .filter((image) => image.url)
    .sort(
      (left, right) =>
        left.sortOrder - right.sortOrder ||
        left.filename.localeCompare(right.filename) ||
        left.id.localeCompare(right.id),
    );

  if (visibleImages.length === 0) {
    return <span className="cp-empty-value">None</span>;
  }

  return (
    <div className="cp-activity-images">
      {visibleImages.map((image) => (
        <figure className="cp-activity-image" key={image.id}>
          <a
            href={image.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${image.filename || "activity image"}`}
          >
            <img
              src={image.url}
              alt={image.filename || "Activity image"}
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
            />
          </a>
          <figcaption>{image.filename || "Activity image"}</figcaption>
        </figure>
      ))}
    </div>
  );
}

export function ActivityImageUploadField({
  images,
  disabled = false,
  onUpload,
  onRemove,
}: {
  images: readonly PortalActivityImage[];
  disabled?: boolean;
  onUpload: (file: File) => Promise<void>;
  onRemove: (imageId: string) => Promise<void>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [pending, setPending] = useState<
    { kind: "upload" } | { kind: "remove"; imageId: string } | null
  >(null);
  const [error, setError] = useState<string>();
  const busy = pending !== null;

  const uploadFiles = async (files: readonly File[]) => {
    if (disabled || busy || files.length === 0) return;
    setError(undefined);
    setPending({ kind: "upload" });
    try {
      for (const file of files) {
        const validationMessage = activityImageValidationMessage(file);
        if (validationMessage) throw new Error(validationMessage);
        await onUpload(file);
      }
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "The image could not be uploaded.",
      );
    } finally {
      setPending(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleFileSelection = (event: ChangeEvent<HTMLInputElement>) => {
    void uploadFiles(Array.from(event.target.files ?? []));
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    void uploadFiles(Array.from(event.dataTransfer.files));
  };

  const removeImage = async (image: PortalActivityImage) => {
    if (disabled || busy) return;
    setError(undefined);
    setPending({ kind: "remove", imageId: image.id });
    try {
      await onRemove(image.id);
    } catch (removeError) {
      setError(
        removeError instanceof Error
          ? removeError.message
          : "The image could not be removed.",
      );
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="cp-activity-image-editor" aria-busy={busy}>
      {images.length ? (
        <div className="cp-activity-images">
          {images.map((image) => (
            <figure
              className="cp-activity-image cp-activity-image--editable"
              key={image.id}
            >
              <a
                href={image.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${image.filename || "activity image"}`}
              >
                <img
                  src={image.url}
                  alt={image.filename || "Activity image"}
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                />
              </a>
              <figcaption>{image.filename || "Activity image"}</figcaption>
              <button
                type="button"
                className="cp-activity-image__remove"
                aria-label={`Remove ${image.filename || "activity image"}`}
                disabled={disabled || busy}
                onClick={() => void removeImage(image)}
              >
                <Trash2 aria-hidden="true" />
                {pending?.kind === "remove" && pending.imageId === image.id
                  ? "Removing…"
                  : "Remove"}
              </button>
            </figure>
          ))}
        </div>
      ) : (
        <span className="cp-empty-value">None</span>
      )}

      <div
        className="cp-activity-image-dropzone"
        data-active={dragActive}
        onDragEnter={(event) => {
          event.preventDefault();
          if (!disabled && !busy) setDragActive(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          if (
            !event.currentTarget.contains(event.relatedTarget as Node | null)
          ) {
            setDragActive(false);
          }
        }}
        onDrop={handleDrop}
      >
        <ImagePlus aria-hidden="true" />
        <div>
          <strong>Drop images here</strong>
          <span>JPEG, PNG, WebP, or GIF. Maximum 10 MB each.</span>
        </div>
        <button
          type="button"
          className="cp-button cp-button--secondary"
          disabled={disabled || busy}
          onClick={() => inputRef.current?.click()}
        >
          {pending?.kind === "upload" ? "Uploading…" : "Choose images"}
        </button>
        <input
          ref={inputRef}
          className="cp-visually-hidden"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          disabled={disabled || busy}
          aria-label="Choose Activity Images"
          onChange={handleFileSelection}
        />
      </div>
      {error ? (
        <p className="cp-form-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
